/**
 * Controlled service-worker reload (T016-FE, plan l.74).
 *
 * The upgrade/maintenance state must offer a reload that ACTUALLY replaces the
 * controlling worker rather than a plain `location.reload()`, which under a
 * stale/legacy worker can re-activate the same old bundle (the classic
 * "reload does nothing" PWA trap).
 *
 * Contract:
 * 1. Ask any WAITING worker to activate via the standard `SKIP_WAITING`
 *    message. A worker generated before this message existed ignores it —
 *    harmless, step 3 still gets a fresh document.
 * 2. Delete the public discovery snapshots from the `api-cache` entry of
 *    Cache Storage so the reloaded tab cannot boot from a pre-cutover body.
 *    The authenticated (`/api/creator|admin|me`) and location POSTs are
 *    NetworkOnly in the worker and never live in this cache, so they are
 *    untouched by construction.
 * 3. Delegate to `vite-plugin-pwa`'s own update handle (registered by
 *    `main.tsx`), which waits for the new worker and reloads. Fall back to a
 *    plain reload when no handle is registered (organizer host, tests).
 *
 * Every step is best-effort: a failure of one must not strand the user on the
 * upgrade screen, so the fallback always ends in a navigation.
 */

type UpdateSW = (reloadPage?: boolean) => Promise<void>;

/**
 * Fallback-navigation delay (M2 of the T016 audit): `updateSW(true)` resolves
 * WITHOUT navigating when no worker update is pending (the served bundle is
 * already the latest). The click must still visibly do something — a reload
 * revalidates the document — so a one-shot `location.reload()` is scheduled
 * and cancelled when a real update takes control (`controllerchange`), which
 * means the plugin's own reload owns the navigation.
 */
export const RELOAD_FALLBACK_MS = 2000;

/** Purge only the PUBLIC discovery snapshots; never auth/location traffic. */
const PUBLIC_DISCOVERY_PREFIXES = ['/api/events', '/api/territory', '/api/cities'];

let updateSW: UpdateSW | null = null;

/** Register the plugin's update handle. Called once by `main.tsx`. */
export function registerSwUpdateHandle(handle: UpdateSW): void {
  updateSW = handle;
}

/** Test-only: drop the registered handle between tests. */
export function resetSwUpdateHandle(): void {
  updateSW = null;
}

/**
 * Delete the public discovery snapshots from the `api-cache` entry. Exported
 * because `DiscoveryContext` runs the same purge when the registry version
 * changes mid-session (reference change), not only on worker activation.
 */
export async function purgePublicApiSnapshots(): Promise<void> {
  const cachesApi = globalThis.caches;
  if (!cachesApi || typeof cachesApi.open !== 'function') return;
  try {
    const cache = await cachesApi.open('api-cache');
    const keys = await cache.keys();
    await Promise.all(
      keys.map((request) => {
        const url = new URL(request.url);
        if (PUBLIC_DISCOVERY_PREFIXES.some((prefix) => url.pathname.startsWith(prefix))) {
          return cache.delete(request);
        }
        return Promise.resolve(false);
      })
    );
  } catch {
    // Cache Storage can be unavailable (private mode, disabled) — the reload
    // below still fetches fresh bodies, so a purge failure is not fatal.
  }
}

async function activateWaitingWorkers(): Promise<void> {
  const registrations = await navigator.serviceWorker?.getRegistrations?.();
  if (!registrations) return;
  await Promise.all(
    registrations.map(async (registration) => {
      registration.waiting?.postMessage({ type: 'SKIP_WAITING' });
    })
  );
}

/**
 * The upgrade screen's "Aggiorna" action: activate waiting workers, purge the
 * public snapshots, then reload through the plugin handle (or plain reload).
 * ALWAYS ends in a navigation (M2 of the T016 audit): when the handle finds
 * nothing to activate it resolves without navigating, so a one-shot fallback
 * reload is scheduled and cancelled if a real worker takeover fires
 * `controllerchange` (the plugin's own reload then owns the navigation).
 */
export async function requestControlledReload(): Promise<void> {
  try {
    await activateWaitingWorkers();
  } catch {
    // No Service Worker container (tests, insecure context) — keep going.
  }
  await purgePublicApiSnapshots();

  const serviceWorker = navigator.serviceWorker;
  let fallback: ReturnType<typeof setTimeout> | null = null;
  const cancelFallback = () => {
    if (fallback !== null) {
      clearTimeout(fallback);
      fallback = null;
    }
  };
  // A real takeover means the new worker is in control; the plugin (autoUpdate)
  // reloads on its own, so the fallback must not fire a second navigation.
  try {
    serviceWorker?.addEventListener?.('controllerchange', cancelFallback, { once: true });
  } catch {
    // No Service Worker container — the fallback below still navigates.
  }

  if (!updateSW) {
    // No plugin handle (organizer host, tests): a plain reload is the whole
    // contract — nothing was scheduled, so nothing to cancel.
    window.location.reload();
    return;
  }
  try {
    fallback = setTimeout(() => {
      fallback = null;
      window.location.reload();
    }, RELOAD_FALLBACK_MS);
    await updateSW(true);
  } catch {
    cancelFallback();
    window.location.reload();
  }
}
